import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  InvestorProfile,
  InvestorProfileUpdate,
  Subscription,
  Transaction,
  ChangePasswordData,
} from './types';
import { APIError, User } from '../user/types';
import { apiFetchJson } from 'src/utils/api';

// --- Personal Info (uses existing PATCH /users) ---

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  city?: string;
  language?: string;
}

export const updateUserProfile = createAsyncThunk<
  User,
  { data: UpdateProfileData; accessToken: string },
  { rejectValue: APIError }
>('settings/updateProfile', async ({ data, accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<User>('/users', accessToken, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to update profile' });
  }
});

// --- Change Password ---

export const changePassword = createAsyncThunk<
  { message: string },
  { data: ChangePasswordData; accessToken: string },
  { rejectValue: APIError }
>('settings/changePassword', async ({ data, accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<{ message: string }>('/settings/change-password', accessToken, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to change password' });
  }
});

// --- Marketing Emails (uses PATCH /users) ---

export const updateMarketingEmails = createAsyncThunk<
  User,
  { enabled: boolean; accessToken: string },
  { rejectValue: APIError }
>('settings/updateMarketingEmails', async ({ enabled, accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<User>('/users', accessToken, {
      method: 'PATCH',
      body: JSON.stringify({ marketing_emails: enabled }),
    });
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to update email preferences' });
  }
});

// --- Deactivate Account ---

export const deactivateAccount = createAsyncThunk<
  { message: string },
  { accessToken: string },
  { rejectValue: APIError }
>('settings/deactivateAccount', async ({ accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<{ message: string }>('/settings/deactivate-account', accessToken, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to deactivate account' });
  }
});

// --- Investor Profile ---

export const getInvestorProfile = createAsyncThunk<
  InvestorProfile,
  { accessToken: string },
  { rejectValue: APIError }
>('settings/getInvestorProfile', async ({ accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<InvestorProfile>('/settings/investor-profile', accessToken);
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to fetch investor profile' });
  }
});

export const updateInvestorProfile = createAsyncThunk<
  InvestorProfile,
  { data: InvestorProfileUpdate; accessToken: string },
  { rejectValue: APIError }
>('settings/updateInvestorProfile', async ({ data, accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<InvestorProfile>('/settings/investor-profile', accessToken, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to update investor profile' });
  }
});

// --- Subscription ---

export const getSubscription = createAsyncThunk<
  Subscription,
  { accessToken: string },
  { rejectValue: APIError }
>('settings/getSubscription', async ({ accessToken }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<Subscription>('/settings/subscription', accessToken);
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to fetch subscription' });
  }
});

// --- Transactions ---

export const getTransactions = createAsyncThunk<
  { transactions: Transaction[]; total: number },
  { accessToken: string; skip?: number; limit?: number },
  { rejectValue: APIError }
>('settings/getTransactions', async ({ accessToken, skip = 0, limit = 20 }, { rejectWithValue }) => {
  try {
    return await apiFetchJson<{ transactions: Transaction[]; total: number }>(
      `/settings/transactions?skip=${skip}&limit=${limit}`,
      accessToken,
    );
  } catch (error) {
    return rejectWithValue({ detail: (error as Error).message || 'Failed to fetch transactions' });
  }
});
